import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["loop", "trapped", "onMountAutoFocus", "onUnmountAutoFocus"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import * as React from 'react';
import { focusScopesStack, removeLinks } from './focusScopesStack';
import { focus, focusFirst, getTabbableCandidates, getTabbableEdges } from './utils';
import { DomPrimitive } from '../DomPrimitive';
import { useCallbackRef } from '../../hooks/useCallbackRef';
import { useComposedRefs } from '../../hooks/useComposedRefs';
import { jsx as _jsx } from "react/jsx-runtime";
const AUTOFOCUS_ON_MOUNT = 'focusScope.autoFocusOnMount';
const AUTOFOCUS_ON_UNMOUNT = 'focusScope.autoFocusOnUnmount';
const EVENT_OPTIONS = {
  bubbles: false,
  cancelable: true
};
const FOCUS_SCOPE_NAME = 'FocusScope';
export const FocusScope = /*#__PURE__*/React.forwardRef((props, forwardedRef) => {
  const {
      loop = false,
      trapped = false,
      onMountAutoFocus: onMountAutoFocusProp,
      onUnmountAutoFocus: onUnmountAutoFocusProp
    } = props,
    scopeProps = _objectWithoutProperties(props, _excluded);
  const [container, setContainer] = React.useState(null);
  const onMountAutoFocus = useCallbackRef(onMountAutoFocusProp);
  const onUnmountAutoFocus = useCallbackRef(onUnmountAutoFocusProp);
  const lastFocusedElementRef = React.useRef(null);
  const composedRefs = useComposedRefs(forwardedRef, node => setContainer(node));
  const focusScope = React.useRef({
    paused: false,
    pause() {
      this.paused = true;
    },
    resume() {
      this.paused = false;
    }
  }).current;

  // Обеспечивает захват фокуса, если фокус перемещается программно
  React.useEffect(() => {
    // eslint-disable-next-line no-inner-declarations
    function handleFocusIn(event) {
      if (focusScope.paused || !container) {
        return;
      }
      const target = event.target;
      if (container.contains(target)) {
        lastFocusedElementRef.current = target;
      } else {
        focus(lastFocusedElementRef.current, {
          select: true
        });
      }
    }

    // eslint-disable-next-line no-inner-declarations
    function handleFocusOut(event) {
      if (focusScope.paused || !container) {
        return;
      }
      const relatedTarget = event.relatedTarget;

      // Событие `focusout` с `null` в `relatedTarget` произойдет как минимум в двух случаях:
      //
      // 1. Когда пользователь переключает приложение/вкладки/окна/сам браузер теряет фокус.
      // 2. В Google Chrome, когда фокусированный элемент удаляется из DOM.
      //
      // Мы позволяем браузеру делать свое дело здесь, потому что:
      //
      // 1. Браузер уже запоминает, что было в фокусе, когда страница снова получает фокус.
      // 2. В Google Chrome, если мы попытаемся сфокусироваться на удаленном элементе (как указано ниже), это
      //    приводит к загрузке ЦП на 100%, поэтому мы избегаем этого по этой причине.
      if (relatedTarget === null) {
        return;
      }

      // Если фокус переместился на фактический допустимый элемент (`relatedTarget !== null`)
      // который находится за пределами контейнера, мы перемещаем фокус на последний допустимый фокусированный элемент внутри.
      if (!container.contains(relatedTarget)) {
        focus(lastFocusedElementRef.current, {
          select: true
        });
      }
    }

    // Когда фокусированный элемент удаляется из DOM, браузеры перемещают фокус
    // обратно на document.body. В этом случае мы перемещаем фокус на контейнер
    // чтобы правильно захватить фокус.
    // eslint-disable-next-line no-inner-declarations
    function handleMutations(mutations) {
      const focusedElement = document.activeElement;
      if (focusedElement !== document.body) {
        return;
      }
      for (const mutation of mutations) {
        if (mutation.removedNodes.length > 0) {
          focus(container);
        }
      }
    }
    let mutationObserver;
    if (trapped) {
      document.addEventListener('focusin', handleFocusIn);
      document.addEventListener('focusout', handleFocusOut);
      const mutationObserver = new MutationObserver(handleMutations);
      if (container) {
        mutationObserver.observe(container, {
          childList: true,
          subtree: true
        });
      }
    }
    return () => {
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
      mutationObserver === null || mutationObserver === void 0 || mutationObserver.disconnect();
    };
  }, [trapped, container, focusScope.paused]);
  React.useEffect(() => {
    if (container) {
      focusScopesStack.add(focusScope);
      const previouslyFocusedElement = document.activeElement;
      const hasFocusedCandidate = container.contains(previouslyFocusedElement);
      if (!hasFocusedCandidate) {
        const mountEvent = new CustomEvent(AUTOFOCUS_ON_MOUNT, EVENT_OPTIONS);
        container.addEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);
        container.dispatchEvent(mountEvent);
        if (!mountEvent.defaultPrevented) {
          focusFirst(removeLinks(getTabbableCandidates(container)), {
            select: true
          });
          if (document.activeElement === previouslyFocusedElement) {
            focus(container);
          }
        }
      }
      return () => {
        container.removeEventListener(AUTOFOCUS_ON_MOUNT, onMountAutoFocus);

        // Мы столкнулись с ошибкой React (исправлено в v17) с фокусировкой при размонтировании.
        // Нам нужно немного задержать фокус, чтобы обойти это на данный момент.
        // См.: https://github.com/facebook/react/issues/17894
        setTimeout(() => {
          const unmountEvent = new CustomEvent(AUTOFOCUS_ON_UNMOUNT, EVENT_OPTIONS);
          container.addEventListener(AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
          container.dispatchEvent(unmountEvent);
          if (!unmountEvent.defaultPrevented) {
            focus(previouslyFocusedElement !== null && previouslyFocusedElement !== void 0 ? previouslyFocusedElement : document.body, {
              select: true
            });
          }
          // нам нужно удалить слушатель после `dispatchEvent`
          container.removeEventListener(AUTOFOCUS_ON_UNMOUNT, onUnmountAutoFocus);
          focusScopesStack.remove(focusScope);
        }, 0);
      };
    }
    return;
  }, [container, onMountAutoFocus, onUnmountAutoFocus, focusScope]);

  // Обеспечивает зацикливание фокуса (при переходе по табуляции на краях)
  const handleKeyDown = React.useCallback(event => {
    if (!loop && !trapped) {
      return;
    }
    if (focusScope.paused) {
      return;
    }
    const isTabKey = event.key === 'Tab' && !event.altKey && !event.ctrlKey && !event.metaKey;
    const focusedElement = document.activeElement;
    if (isTabKey && focusedElement) {
      const container = event.currentTarget;
      const [first, last] = getTabbableEdges(container);
      const hasTabbableElementsInside = first && last;

      // мы можем зациклить фокус только если у нас есть табулируемые края
      if (!hasTabbableElementsInside) {
        if (focusedElement === container) {
          event.preventDefault();
        }
      } else {
        if (!event.shiftKey && focusedElement === last) {
          event.preventDefault();
          if (loop) {
            focus(first, {
              select: true
            });
          }
        } else if (event.shiftKey && focusedElement === first) {
          event.preventDefault();
          if (loop) {
            focus(last, {
              select: true
            });
          }
        }
      }
    }
  }, [loop, trapped, focusScope.paused]);
  return /*#__PURE__*/_jsx(DomPrimitive.div, _objectSpread(_objectSpread({
    tabIndex: -1
  }, scopeProps), {}, {
    ref: composedRefs,
    onKeyDown: handleKeyDown
  }));
});
FocusScope.displayName = FOCUS_SCOPE_NAME;